import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Typography,
  Button,
  IconButton,
  Badge,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import NotificationsIcon from "@mui/icons-material/Notifications";
import "./layout.css";
import { logoutUser } from "../services/account.service";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useUser } from "../context/UserContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {ROLES} from "../constants/general.constant";
import { getUnreadCount } from "../services/inbox.service";
import logo from "../assets/transparent-logo.png";
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';

interface NavbarProps {
  onMobileToggle: (open: boolean) => void;
  mobileOpen: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onMobileToggle, mobileOpen }) => {
  const navigate = useNavigate();
  const { user: userData } = useUser();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = React.useState<number>(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleMobileSidebarToggle = () => {
    onMobileToggle(!mobileOpen);
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Add your navigation or logout logic here
  const handleProfile = () => {
    handleClose();
    if(userData && userData.role === ROLES.BUSINESS){
      navigate("/business-profile");
    }else if(userData && userData.role === ROLES.BANKER){
      navigate("/banker/profile");
    }else if(userData && userData.role === ROLES.CO_OWNER){
      navigate("/co-owner/your-profile");
    }else if(userData && userData.role === ROLES.CUSTOMER){
      navigate("/customer/profile");
    }else if(userData && userData.role === ROLES.EMPLOYEE){
      navigate("/employee/profile");
    }
  };
  const handleLogout = async () => {
    await logoutUser();
    handleClose();
    navigate("/login");
  };

  const handleNotification = () => {
    if (userData?.role === ROLES.CUSTOMER) {
      navigate("/customer/notifications");
    } else if (userData?.role === ROLES.BANKER) {
      navigate("/banker/notifications");
    } else if (userData?.role === ROLES.CO_OWNER) {
      navigate("/co-owner/notifications");
    } else if (userData?.role === ROLES.EMPLOYEE) {
      navigate("/employee/notifications");
    } else if (userData?.role === ROLES.ADMIN) {
      navigate("/admin/notifications");
    } else {
      navigate("/notifications");
    }
  }
  
  React.useEffect(() => {
    const fetchUnreadCount = async () => {
      if (userData) {
        try {
          const response = await getUnreadCount();
          setUnreadCount(response?.count || 0);
        } catch (error) {
          console.error("Error fetching unread count:", error);
        }
      }
    };

    if (userData) {
      fetchUnreadCount();
    }
  }, [userData]);

  return (
    <AppBar position="static" className="navbar-root" elevation={0}>
      <Toolbar sx={{ justifyContent: "space-between", minHeight: 64 }}>
        {/* Left: Mobile toggle */}
        {isMobile && (
          <IconButton
            color="inherit"
            onClick={handleMobileSidebarToggle}
            aria-label={mobileOpen ? "Close sidebar" : "Open sidebar"}
            sx={{ mr: 1 }}
          >
            {mobileOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
        )}
        {/* Left: Logo and Title */}
        <Box sx={{ display: "flex", alignItems: "center" }}></Box>
        {/* Right: Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Notification Icon */}
          {userData && (
            <Tooltip title="Notifications">
              <IconButton color="default" onClick={() => handleNotification()}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          {/* Avatar + Name Dropdown */}
          <Tooltip title="Account">
            <Box
              onClick={handleAvatarClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                ml: 1,
                px: 1,
                py: 0.5,
                borderRadius: 8,
                cursor: "pointer",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box
                sx={{
                  width: 50,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <img
                  src={userData?.logo || logo}
                  alt="Profile"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography
                  fontWeight={600}
                  fontSize="0.85rem"
                  lineHeight={1.2}
                  noWrap
                  maxWidth={120}
                >
                  {`${(userData as any)?.firstName || ""} ${(userData as any)?.lastName || ""}`.trim() ||
                    userData?.name ||
                    "User"}
                </Typography>
              </Box>
              <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            </Box>
          </Tooltip>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                minWidth: isMobile ? 200 : 240,
                borderRadius: 2,
                mt: 1,
                boxShadow: theme.shadows[3],
              },
            }}
          >

            <MenuItem
              onClick={handleProfile}
              sx={{
                fontSize: "0.9rem",
                py: 1.25,
                px: 2,
                color: "text.primary",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <PersonIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
              My Profile
            </MenuItem>

            <Divider sx={{ my: 0.5 }} />

            <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                size="small"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ borderRadius: 1.5, fontWeight: 600, textTransform: "none" }}
              >
                Logout
              </Button>
            </Box>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
